'use client';

import { c as _c } from "react/compiler-runtime";
import { css } from '@repo/pandacss/css';
import { Button } from '@repo/ui/Button';
import { LoadingSpinner } from '@repo/ui/LoadingSpinner';
import { Typography } from '@repo/ui/Typography';
import { useRef } from 'react';
import type { HydratedStepConfig } from '@/features/funnel/config/types';
import { useAnswer } from '@/features/funnel/state/answer/hooks';
import { useFunnelStepId, useIsBranchLastStep, useIsFunnelSubmitting } from '@/features/funnel/state/funnel/Context/hooks';
import { useFunnelNavigation } from '@/features/funnel/state/funnel/useFunnelNavigation';
import { flattenOptions } from '../../Form/SearchableGroupedMultiSelectInput/filterGroups';
import { SelectedChips } from '../../Form/SearchableGroupedMultiSelectInput/SelectedChips';
import { getFunnelStepFormId, pendingFieldsetClass } from '../../Form/utils';
import { useVisualViewportKeyboardOffset } from './useVisualViewportKeyboardOffset';
type Props = {
  step: HydratedStepConfig;
  isStepSkippable: boolean;
  isAutoAdvance?: boolean;
};
export function Footer(t0) {
  const $ = _c(37);
  const {
    step,
    isStepSkippable,
    isAutoAdvance: t1
  } = t0;
  const isAutoAdvance = t1 === undefined ? false : t1;
  const footerRef = useRef(null);
  const keyboardOffset = useVisualViewportKeyboardOffset(footerRef);
  const stepId = useFunnelStepId();
  const {
    value,
    setAnswer
  } = useAnswer(stepId);
  const {
    advance
  } = useFunnelNavigation();
  const isLast = useIsBranchLastStep();
  const isSubmitting = useIsFunnelSubmitting();
  const hasAnswer = Array.isArray(value) ? value.length > 0 : value !== undefined;
  let t2;
  if ($[0] !== advance || $[1] !== isStepSkippable || $[2] !== step || $[3] !== stepId) {
    t2 = isStepSkippable ? () => {
      advance(stepId, ["searchable-grouped-multi-select", "multi-select"].includes(step.type) ? [] : undefined);
    } : undefined;
    $[0] = advance;
    $[1] = isStepSkippable;
    $[2] = step;
    $[3] = stepId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onSkip = t2;
  if (isAutoAdvance && !isStepSkippable) {
    return null;
  }
  const isSearchableMultiSelect = step.type === "searchable-grouped-multi-select";
  let t3;
  if ($[5] !== isSearchableMultiSelect || $[6] !== step.asyncOptions || $[7] !== value) {
    t3 = isSearchableMultiSelect && Array.isArray(value) && value.length > 0 ? flattenOptions(step.asyncOptions).filter(option => value.includes(option.value)) : [];
    $[5] = isSearchableMultiSelect;
    $[6] = step.asyncOptions;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const selectedChips = t3;
  const chipVariant = isSearchableMultiSelect ? step.chipVariant : "circle";
  let t4;
  if ($[9] !== setAnswer || $[10] !== value) {
    t4 = chipValue => {
      if (!Array.isArray(value)) {
        return;
      }
      setAnswer(value.filter(item => item !== chipValue));
    };
    $[9] = setAnswer;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const removeChip = t4;
  let t5;
  if ($[12] !== keyboardOffset) {
    t5 = keyboardOffset > 0 ? {
      transform: `translateY(${keyboardOffset}px)`
    } : undefined;
    $[12] = keyboardOffset;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css({
      position: "sticky",
      bottom: "0",
      left: "0",
      bg: "common.white",
      isolation: "isolate",
      zIndex: "[40]",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      overflow: {
        base: "scroll",
        md: "auto"
      },
      _before: {
        content: "\"\"",
        position: "absolute",
        top: "0",
        left: "[calc(50% - 50cqw - max(0px, (100vw - var(--wrapper-max)) / 2))]",
        width: "[calc(100cqw + max(0px, (100vw - var(--wrapper-max)) / 2))]",
        height: "[100%]",
        bg: "common.white",
        zIndex: "-1",
        boxShadow: "[0 -10px 10px 0 rgba(100, 102, 106, 0.05)]",
        borderTop: "[1px solid {colors.neutral.lighter-2}]"
      }
    });
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== chipVariant || $[16] !== isSubmitting || $[17] !== removeChip || $[18] !== selectedChips) {
    t7 = selectedChips.length > 0 && <fieldset disabled={isSubmitting} className={pendingFieldsetClass}><SelectedChips selected={selectedChips} onRemove={removeChip} variant={chipVariant} /></fieldset>;
    $[15] = chipVariant;
    $[16] = isSubmitting;
    $[17] = removeChip;
    $[18] = selectedChips;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = css({
      width: "[100%]",
      maxWidth: "[calc(640px - 1rem)]",
      mx: "auto",
      p: "3",
      display: "flex",
      flexDirection: "column",
      gap: "2"
    });
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== hasAnswer || $[22] !== isAutoAdvance || $[23] !== isLast || $[24] !== isStepSkippable || $[25] !== isSubmitting || $[26] !== onSkip || $[27] !== stepId) {
    t9 = isStepSkippable && !hasAnswer && onSkip ? <button type="button" className={css({
      bg: "[transparent]",
      border: "none",
      cursor: "pointer",
      py: "2",
      alignSelf: "center",
      _hover: {
        textDecoration: "underline"
      }
    })} onClick={onSkip} disabled={isSubmitting}><Typography as="span" variant="body" css={{
        color: "greens.dark",
        fontWeight: "semibold"
      }}>Skip this step</Typography></button> : !isAutoAdvance && <Button type="submit" form={getFunnelStepFormId(stepId)} size="medium" disabled={isSubmitting} aria-busy={isSubmitting} aria-label={isSubmitting ? "Loading" : undefined} className={css({
      textTransform: "uppercase",
      height: "var(--next-button-height)",
      fontSize: "[14px]",
      "&[aria-busy=true]:disabled": {
        bg: "greens.main",
        color: "common.white",
        cursor: "progress"
      },
      _focusVisible: {
        outlineOffset: "[4px]"
      }
    })}>{isSubmitting ? <LoadingSpinner variant="onPrimaryButton" /> : isLast ? "See results" : "Next"}</Button>;
    $[21] = hasAnswer;
    $[22] = isAutoAdvance;
    $[23] = isLast;
    $[24] = isStepSkippable;
    $[25] = isSubmitting;
    $[26] = onSkip;
    $[27] = stepId;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t8 || $[30] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7) {
    t11 = <div ref={footerRef} style={t5} className={t6}>{t7}{t10}</div>;
    $[32] = t10;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}